import { Component, h } from 'preact';
import { InquiryDocument } from 'inquiries/inquiry-document.model';
import { Inquiry } from '../../../../../types/inquiries';
import { InquiryDocumentType }  from '../../../../../constants/inquiries';
import { formatAsColloquialDate } from '../../../../core/date-formatter';
import { inquiryService } from '../../../../utils/inquiry.service';
import { ReqState } from '../../../../../types';
import { InquiryLoadingMessage, InquiryLoadFailureMessage } from './loaders';
import { DocumentShareResult } from 'inquiries/document-share-result.model';

interface InquiryDetailsDocumentListProps {
  inquiry: Inquiry,
  documents: InquiryDocument[];
  documentType: InquiryDocumentType;
  reqState: ReqState;

  onEditClick: (documentItem: InquiryDocument) => void;
  onStatusClick: (documentItem: InquiryDocument) => void;
}

interface InquiryDetailsDocumentListState {
  dlForShareState?: ReqState;
}


const DOCUMENT_INDEX_PATTERN = /^[a-zA-Z]+(\d+)/;

export class InquiryDetailsDocumentList extends Component<InquiryDetailsDocumentListProps, InquiryDetailsDocumentListState> {
  getShareLink(result: DocumentShareResult) {
    const { inquiryCode, documentType, token } = result;
    const url = `https://www.hoppler.com.ph/document-share/${inquiryCode}/${documentType}/${token}`;

    return url;
  }

  async onShareClick(doc: InquiryDocument) {
    const documentType = this.props.documentType;
    const nav = window.navigator as any;

    try {
      this.setState({ dlForShareState: ReqState.IN_PROGRESS });

      const docTypeLabel = documentType === InquiryDocumentType.OFFER ? 'Offer' : 'Contract';
      const result = await inquiryService.generateDocumentShareToken(this.props.inquiry, doc);
      const url = this.getShareLink(result);

      // console.log('Share!', url);
      nav.share({
        title: `${docTypeLabel} Document Share Link for ${doc.documentName}`,
        url
      });

      this.setState({ dlForShareState: ReqState.SUCCESS });
    } catch (err) {
      console.error(err);
      this.setState({ dlForShareState: ReqState.FAILED });
    }
  }

  getDocumentDownloadLink(doc: InquiryDocument) {
    const { inquiry } = this.props;

    // Generated offer/contract documents are only supported for residential
    // inquiries.
    if (inquiry.division === 'Residential') {
      const link = inquiryService.getDocumentDownloadLink(inquiry, doc);

      return (
        <a className="btn btn-link hi-inqdetdoclist__button"
          href={ link }
          target='_blank' rel='noopener'>
          download
        </a>
      );
    }
  }

  getDocumentShareLink(doc: InquiryDocument) {
    const { inquiry } = this.props;
    const nav = window.navigator as any;
    const canShare = !!nav.share;

    const downloading = this.state.dlForShareState === ReqState.IN_PROGRESS;

    // Generated offer/contract documents are only supported for residential
    // inquiries.
    if (inquiry.division === 'Residential' && canShare) {
      return (
        <button className="btn btn-link hi-inqdetdoclist__button"
          type="button"
          disabled={ downloading }
          onClick={ () => this.onShareClick(doc) }>
          { downloading ? 'sharing...' : 'share' }
        </button>
      );
    }
  }

  getDocumentLabel(doc: InquiryDocument) {
    const docType = this.props.documentType;
    let typeLabel: string, key: string;

    if (docType === InquiryDocumentType.OFFER) {
      typeLabel = 'LOI';
      key = doc.loiKey;
    } else {
      typeLabel = 'Contract';
      key = doc.contractKey;
    }

    const [ matched, idxString ] = key.match(DOCUMENT_INDEX_PATTERN);
    return `${typeLabel} ${parseInt(idxString)}`;
  }

  getOfferSentRow(doc: InquiryDocument) {
    if (this.props.documentType === InquiryDocumentType.OFFER) {
      if (doc.dateSent) {
        return (
          <div className="hi-inqdetdoclist__date-sent-row">
            <i className="fas fa-paperclip" />&nbsp;
            Sent Offer ({ formatAsColloquialDate(doc.dateSent) })
          </div>
        );
      }
    }
  }

  getOfferAcceptedRow(doc: InquiryDocument) {
    if (this.props.documentType === InquiryDocumentType.OFFER && doc.dateSigned) {
      if (doc.dateSent) {
        return (
          <div className="hi-inqdetdoclist__date-sent-row">
            <i className="fas fa-paperclip" />&nbsp;
            Accepted Offer ({ formatAsColloquialDate(doc.dateSigned) })
          </div>
        );
      }
    }
  }

  getStatusLink(doc: InquiryDocument) {
    const props = this.props;

    if (props.documentType === InquiryDocumentType.OFFER && !doc.dateSent) {
      return null;
    } else {
      return (
        <a href='#' className="btn btn-link hi-inqdetdoclist__button hi-inqdetdoclist__button--upload-button-align"
          onClick={ e => { e.preventDefault(); props.onStatusClick(doc) } }>
          status
        </a>
      );
    }
  }

  render(props: InquiryDetailsDocumentListProps) {
    const documents = props.documents || [];

    if (props.reqState === ReqState.IN_PROGRESS) {
      return <InquiryLoadingMessage message='Loading documents...' />;
    } else if (props.reqState === ReqState.FAILED) {
      return <InquiryLoadFailureMessage message='Could not load documents' />;
    }

    if (documents.length > 0) {
      return (
        <div className="hi-inqdetdoclist">
          { documents.map(doc => (
            <div className="hi-inqdetdoclist__item">
              <div className="hi-inqdetdoclist__loi-row">
                <button className="hi-inqdetdoclist__document-name" onClick={ () => props.onEditClick(doc) }>
                  { this.getDocumentLabel(doc) }
                </button>

                <div className="hi-inqdetdoclist__buttons">
                  { this.getDocumentDownloadLink(doc) }
                  { this.getDocumentShareLink(doc) }
                  { this.getStatusLink(doc) }
                </div>
              </div>

              { this.getOfferSentRow(doc) }
              { this.getOfferAcceptedRow(doc) }
            </div>
          )) }
        </div>
      );
    }
  }
}